import {
  FC, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import useCurrency from 'hooks/useCurreny';
import useMaskedInput from 'hooks/useMaskedInput';

import trashIcon from '/trashHelp.svg';

import Select from 'components/Select';
import {
  CropProps, CropTableProps,
} from 'utils/types';
import Input from 'components/Input';
import Button from 'components/Button';
import MaskInput from 'components/MaskedInput';
import {
  mtHaToTonAc, mtTot, unMaskInput, unMaskInputValue,
} from 'utils/helpers';
import { FieldProps } from 'features/Croptimizer/types';
import { Metric } from 'utils/constants';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

import { useGenerateCrop } from '../../../hooks/useGenerateCrop';

interface Props {
  setEditCropModal: (value: CropTableProps | null) => void;
  crop?: CropProps[];
  editCropModal: CropTableProps;
  setTableData: (tableDataOrUpdater: CropTableProps[] | ((prev: CropTableProps[]) => CropTableProps[])) => void;
  object: FieldProps['object'];
  cropIndex: number;
}

const EditCropModal: FC<Props> = ({
  setEditCropModal,
  crop = [],
  editCropModal,
  setTableData,
  object,
  cropIndex,
}) => {
  const { t } = useTranslation();
  const { mtHa, metricValue, onlyAc, onlyMt } = useCroptimizer();
  const { generateCrop } = useGenerateCrop();
  const [initEditCropModal, setInitEditCropModal] = useState(editCropModal);

  const onChange = (change: any, name: string) => (value: any) => {
    change(value);
    const currentCrop = crop.find(item => item.Crops === cropType);

    if (currentCrop) {
      const data = {
        yield: +cleanyieldAdd,
        price: +cleanprice,
        n: cleanN,
        p: cleanP,
        k: cleanK,
        [name]: unMaskInput(value),
      };
      const editedTableDataItem = generateCrop(currentCrop, object, editCropModal.id, data, cropIndex);
      setEditCropModal(editedTableDataItem);

      if (name !== 'yield') {
        setYieldAdd(unMaskInputValue(String(editedTableDataItem.yield)));
      }

      if (name !== 'price') {
        setPrice(unMaskInputValue(String(editedTableDataItem.price)));
      }

      if (name !== 'n') {
        setN(unMaskInputValue(String(editedTableDataItem.n)));
      }

      if (name !== 'p') {
        setP(unMaskInputValue(String(editedTableDataItem.p)));
      }

      if (name !== 'k') {
        setK(unMaskInputValue(String(editedTableDataItem.k)));
      }
    }
  };

  const {
    value: yieldAdd,
    setValue: setYieldAdd,
    clean: cleanyieldAdd,
  } = useMaskedInput({
    defaultValue: unMaskInputValue(String(initEditCropModal?.yield)),
  });
  const {
    value: price,
    setValue: setPrice,
    clean: cleanprice,
  } = useMaskedInput({
    defaultValue: unMaskInputValue(String(initEditCropModal?.price)),
  });

  const [cropType, setCropType] = useState(initEditCropModal?.name);

  const onCropChange = ({ value }: {value: string}) => {
    setCropType(value);

    const currentCrop = crop.find(item => item.Crops === value);

    if (currentCrop) {
      const editedTableDataItem = generateCrop({
        ...currentCrop,
        PotentialYeild: metricValue === Metric.metric
          ? currentCrop.PotentialYeild
          : mtHaToTonAc(currentCrop.PotentialYeild),
        Price: metricValue === Metric.metric
          ? currentCrop.Price
          : mtTot(currentCrop.Price),
      }, object, editCropModal.id, undefined, cropIndex);

      setEditCropModal(editedTableDataItem);
      setInitEditCropModal({
        ...editedTableDataItem,
        name: cropType,
      });

      setYieldAdd(unMaskInputValue(String(editedTableDataItem.yield)));
      setPrice(unMaskInputValue(String(editedTableDataItem.price)));
      setN(unMaskInputValue(String(editedTableDataItem.n)));
      setP(unMaskInputValue(String(editedTableDataItem.p)));
      setK(unMaskInputValue(String(editedTableDataItem.k)));
    }
  };

  const {
    value: n,
    setValue: setN,
    clean: cleanN,
  } = useMaskedInput({
    defaultValue: unMaskInputValue(String(initEditCropModal?.n)),
  });
  const {
    value: p,
    setValue: setP,
    clean: cleanP,
  } = useMaskedInput({
    defaultValue: unMaskInputValue(String(initEditCropModal?.p)),
  });
  const {
    value: k,
    setValue: setK,
    clean: cleanK,
  } = useMaskedInput({
    defaultValue: unMaskInputValue(String(initEditCropModal?.k)),
  });
  const { currencyGreen } = useCurrency();

  const selectOptions = useMemo(() => {
    if (crop) {
      return crop?.map((crop) => ({ value: crop.Crops,
        label: crop.Crops }));
    }

    return [];
  }, [crop]);

  const onSubmit = () => {
    const editedData = {
      id: editCropModal?.id,
      name: cropType,
      yield: Number(cleanyieldAdd),
      price: Number(cleanprice),
      phIfNoLim: editCropModal?.phIfNoLim,
      limiting: editCropModal?.limiting,
      expectPh: editCropModal?.expectPh,
      yieldPercent: editCropModal?.yieldPercent,
      yieldPrice: editCropModal?.yieldPrice,
      n: cleanN,
      p: cleanP,
      k: cleanK,
      npkGains: editCropModal?.npkGains,
      monps: editCropModal?.monps,
      total: editCropModal?.total,
    };

    setTableData((prev) => (prev as CropTableProps[]).map((item) => {
      if (item.id === editedData?.id) {
        return editedData;
      }

      return item;
    }));

    setEditCropModal(null);
  };

  const getDisabledState = () => {
    if (initEditCropModal?.name !== cropType) {
      return false;
    }

    if (initEditCropModal?.price !== Number(cleanprice)) {
      return false;
    }

    if (initEditCropModal?.yield !== Number(cleanyieldAdd)) {
      return false;
    }

    if (Number(initEditCropModal?.n) !== Number(cleanN)) {
      return false;
    }

    if (Number(initEditCropModal?.p) !== Number(cleanP)) {
      return false;
    }

    if (Number(initEditCropModal?.k) !== Number(cleanK)) {
      return false;
    }

    return true;
  };

  return (
    <div className="fixed z-50 top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.5)] flex items-center justify-center h-full w-screen overflow-y-scroll">
      <div className="bg-white p-8 md:p-4 rounded-2xl md:rounded-none flex flex-col r w-[649px] sm:w-full md:max-w-90 absolute top-[50%] md:top-0 left-[50%] translate-x-[-50%] 2xl:translate-y-[-50%]">
        <div
          className="flex justify-end items-end w-full cursor-pointer"
          onClick={() => setEditCropModal(null)}
        >
          <img src={trashIcon} alt="" />
        </div>
        <div className="font-bold text-2xl md:text-xl text-center">
          {t('editCrop')}
        </div>
        <div className="flex justify-start items-start w-full flex-col">
          <div className="font-bold mt-6 mb-4">{t('cropRotation')}</div>
          <div className="w-full">
            <Select
              title={t('cropRotation')}
              options={selectOptions}
              onChange={onCropChange}
              value={
                cropType?.length > 0
                  ? [{ value: cropType,
                    label: cropType }]
                  : []
              }
            />
          </div>

          <div className="font-bold mt-6 mb-4">{t('crop')}</div>
          <div className="flex justify-between gap-[15.5px] w-full">
            <MaskInput
              className="w-[285px] sm:w-full md:mb-6"
              title={`${t('yield')} (${mtHa})`}
              titleClassName="top-[-9px] left-[8px]"
              value={String(yieldAdd)}
              onChange={onChange(setYieldAdd, 'yield')}
            />
            <MaskInput
              className="w-[285px] sm:w-full"
              title={`${t('price')} ${currencyGreen}/${onlyMt}`}
              titleClassName="top-[-9px] left-[8px]"
              value={String(price)}
              onChange={onChange(setPrice, 'price')}
            />
          </div>

          <div className="font-bold mt-4 mb-4">{t('soil')} pH</div>
          <div className="flex justify-between gap-[15.5px] md:flex-wrap w-full">
            <Input
              wrapperCn='sm:shrink sm:grow sm:basis-2/5'
              className="w-[184px] md:w-[285px] sm:!w-full md:mb-6 sm:!mb-0"
              title={t('phNolimeapp')}
              titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
              value={unMaskInputValue(
                String(editCropModal?.phIfNoLim ? editCropModal.phIfNoLim.toFixed(2) : '0.1'),
              )}
              disabled
            />
            <Input
              wrapperCn='sm:shrink sm:grow sm:basis-2/5'
              className="w-[184px] md:w-[285px] sm:!w-full md:mb-6 sm:!mb-0"
              title={t('limingStatus')}
              titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
              disabled
              value={editCropModal?.limiting}
            />
            <Input
              wrapperCn='md:w-full'
              className="w-[184px] md:w-full"
              title={t('expect') + ' pH'}
              titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
              disabled
              value={unMaskInputValue(editCropModal?.expectPh)}
            />
          </div>

          <div className="font-bold mt-4 mb-4">{t('yieldGains')}</div>
          <div className="flex justify-between gap-[15.5px] md:flex-wrap sm:!flex-nowrap w-full">
            <Input
              wrapperCn='sm:w-1/2'
              className="w-[285px] sm:w-full md:mb-6 sm!:mb-0"
              title="%"
              titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
              disabled
              value={unMaskInputValue(editCropModal?.yieldPercent)}
            />
            <Input
              wrapperCn='sm:w-1/2'
              className="w-[285px] sm:w-full"
              title={`${currencyGreen}/${onlyAc}`}
              titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
              disabled
              value={unMaskInputValue(String(editCropModal?.yieldPrice))}
            />
          </div>

          <div className="font-bold mt-4 mb-4">
            {t('npkFertilizerdecrease')}
          </div>
          <div className="flex justify-between gap-[15.5px] flex-wrap w-full">
            <MaskInput
              wrapperCn='sm:shrink sm:grow sm:basis-2/5'
              className="w-[184px] md:w-[285px] md:mb-6 sm:!mb-0 sm:!w-full"
              title="N"
              titleClassName="bg-white top-[-9px] left-[8px]"
              value={n}
              onChange={onChange(setN, 'n')}
            />

            <MaskInput
              wrapperCn='sm:shrink sm:grow sm:basis-2/5'
              className="w-[184px] md:w-[285px] md:mb-6 sm:!mb-0 sm:!w-full"
              title={
                <div>
                  P<sub>2</sub>O<sub>5</sub>
                </div>
              }
              titleClassName="bg-white top-[-9px] left-[8px]"
              value={p}
              onChange={onChange(setP, 'p')}
            />

            <MaskInput
              wrapperCn='md:w-full'
              className="w-[184px] md:w-full"
              title={
                <div>
                  K<sub>2</sub>O
                </div>
              }
              titleClassName="bg-white top-[-9px] left-[8px]"
              value={k}
              onChange={onChange(setK, 'k')}
            />
          </div>

          <div className="flex justify-between gap-[15.5px] md:flex-wrap w-full mt-4">
            <div className="flex flex-col sm:w-full">
              <div className="font-bold mb-4">{t('npk')}</div>
              <MaskInput
                wrapperCn='sm:w-full'
                className="w-[184px] md:w-[285px] sm:!w-full"
                title={t('gains') + ` (${currencyGreen}/${onlyAc})`}
                titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
                disabled
                value={String(Math.round(editCropModal?.npkGains))}
              />
            </div>
            <div className="flex flex-col sm:w-full">
              <div className="font-bold mb-4">{t('monps')}</div>
              <MaskInput
                className="w-[184px] md:w-[285px] sm:!w-full"
                title={t('gains') + ` (${currencyGreen}/${onlyAc})`}
                titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
                disabled
                value={String(editCropModal?.monps)}
              />
            </div>
            <div className="flex flex-col md:w-full">
              <div className="font-bold mb-4">{t('totalGains')}</div>
              <MaskInput
                className="w-[184px] md:w-full"
                title={`${currencyGreen}/${onlyAc}`}
                titleClassName="bg-disable-gradient top-[-9px] left-[8px]"
                disabled
                value={String(editCropModal?.total)}
              />
            </div>
          </div>

          <div className="flex md:flex-col mt-4 md:items-center md:mt-6 gap-[15.5px] md:flex-wrap w-full">
            <Button
              onClick={() => setEditCropModal(null)}
              className="w-[288px] sm:w-1/2"
              btnType="secondary"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={onSubmit}
              disabled={getDisabledState()}
              className="w-[288px] sm:w-1/2"
            >
              {t('edit')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCropModal;
